/**
 * Sprint-104d-full — ShipmentTimeline.
 *
 * Vertical 8-step timeline driven by `order.status_timeline` (array of
 * `{status, at, by_user_id, by_role}` from Phase-104) PLUS the legacy
 * fields (`paid_at`, `agent_id`, `status`) for orders predating Phase-104.
 *
 * Determines per-step state ∈ {done, current, future} so the UI can
 * render a filled dot, a pulsing orange dot, or an empty ring.
 */
import { CheckCircle2 } from "lucide-react";

const STEPS = [
  { key: "placed",            label: "Order placed",        match: () => true,                                                 from: (o) => o.created_at },
  { key: "payment_confirmed", label: "Payment confirmed",   match: (o) => o.payment_status === "paid" || o.payment_method === "cod" || !!o.paid_at, from: (o) => o.paid_at },
  { key: "agent_assigned",    label: "Agent assigned",      match: (o) => !!o.agent_id || ["assigned","pickup_assigned","picked_up","in_transit","at_origin_hub","at_dest_hub","out_for_delivery","delivered"].includes(o.status) },
  { key: "picked_up",         label: "Picked up",           match: (o) => ["picked_up","in_transit","at_origin_hub","linehaul","at_dest_hub","out_for_delivery","delivered"].includes(o.status) },
  { key: "at_origin_hub",     label: "At origin hub",       match: (o) => ["at_origin_hub","linehaul","in_transit","at_dest_hub","out_for_delivery","delivered"].includes(o.status) },
  { key: "in_transit",        label: "In transit",          match: (o) => ["in_transit","linehaul","at_dest_hub","out_for_delivery","delivered"].includes(o.status) },
  { key: "out_for_delivery",  label: "Out for delivery",    match: (o) => ["out_for_delivery","delivered"].includes(o.status) },
  { key: "delivered",         label: "Delivered",           match: (o) => o.status === "delivered",                            from: (o) => o.delivered_at },
];

function formatTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function StepDot({ state }) {
  if (state === "done") {
    return (
      <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 ring-4 ring-emerald-50">
        <CheckCircle2 className="h-3 w-3 text-white" />
      </span>
    );
  }
  if (state === "current") {
    return <span className="w-5 h-5 rounded-full bg-[#FF6B00] ring-4 ring-orange-100 animate-pulse shrink-0" />;
  }
  return <span className="w-5 h-5 rounded-full border-2 border-slate-200 bg-white shrink-0" />;
}

function timestampForStep(step, order) {
  // Prefer status_timeline (Phase-104) — the array contains
  // {status, at, by_user_id, by_role}.
  const timeline = Array.isArray(order.status_timeline) ? order.status_timeline : [];
  const match = timeline.find((e) => (e?.status || "").toLowerCase() === step.key);
  if (match?.at) return match.at;
  if (step.from) return step.from(order);
  return null;
}

export default function ShipmentTimeline({ order }) {
  if (!order) return null;
  // Find the last "done" step (highest index where match() returns true).
  let lastDoneIdx = -1;
  STEPS.forEach((s, i) => { if (s.match(order)) lastDoneIdx = i; });

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="shipment-timeline">
      <h2 className="font-['Outfit'] text-base font-bold text-slate-900 mb-4">Shipment timeline</h2>
      <ol className="space-y-3.5">
        {STEPS.map((s, i) => {
          let state = "future";
          if (i < lastDoneIdx) state = "done";
          else if (i === lastDoneIdx) state = (order.status === "delivered" && s.key === "delivered") ? "done" : "current";
          const isLast = i === STEPS.length - 1;
          const ts = state === "future" ? null : timestampForStep(s, order);
          return (
            <li
              key={s.key}
              className="flex items-start gap-3 relative"
              data-testid={`timeline-step-${s.key}`}
              data-state={state}
            >
              <div className="flex flex-col items-center shrink-0">
                <StepDot state={state} />
                {!isLast && (
                  <span
                    className={`w-0.5 grow my-1 ${state === "done" ? "bg-emerald-300" : "bg-slate-200"}`}
                    style={{ minHeight: 20 }}
                  />
                )}
              </div>
              <div className="flex-1 pb-2 min-w-0">
                <p className={`text-sm font-semibold ${state === "future" ? "text-slate-400" : "text-slate-900"}`}>{s.label}</p>
                {state === "current" && (
                  <p className="text-xs text-[#FF6B00] mt-0.5">In progress…</p>
                )}
                {ts && state !== "future" && (
                  <p className="text-xs text-slate-500 mt-0.5">{formatTs(ts)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
